import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Rating,
  Stack,
  Typography,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { togglePlaceFavorite } from '../../services/API/Api';
import StarIcon from '@mui/icons-material/Star';
import Favorite from '@mui/icons-material/Favorite';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';

type PlaceCardProps = {
  imageUrl: string;
  title: string;
  distance: string;
  category: string[];
  tags: string[];
  rating: number;
  islistview: boolean;
  id: string;
  status?: 'approved' | 'pending' | string;
  isLiked?: boolean;
  onFavoriteToggle?: (placeId: string) => void;
  isFavorited?: boolean;
};

const PlaceCard: React.FC<PlaceCardProps> = ({
  imageUrl,
  title,
  distance,
  category,
  tags,
  rating,
  islistview,
  id,
  status,
  isLiked = false,
  onFavoriteToggle,
  isFavorited = false,
}) => {
  const navigate = useNavigate();
  const [isLikedState, setIsLikedState] = useState(isLiked || false);

  const handleFavoriteToggle = async (placeId: string) => {
    try {
      const res = await togglePlaceFavorite(placeId);
      
      if (res.success) {
        // Toggle the isLiked state
        const newIsLiked = !isLikedState;
        setIsLikedState(newIsLiked);
        
        // Call the parent callback if provided
        if (onFavoriteToggle) {
          onFavoriteToggle(placeId);
        }
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  // Dynamically choose a color for the rating badge
  const getRatingColor = (rating: number) => {
    if (rating >= 4) return '#2E7D32'; // green for high ratings
    if (rating >= 3) return '#F9A825'; // amber for medium ratings
    if (rating > 0) return '#EF5350'; // red for low ratings
    return '#BDBDBD'; // grey when there is no rating
  };

  const getStatusStyles = (value?: string) => {
    const normalized = (value || '').toLowerCase();
    if (normalized === 'approved') {
      return { bgcolor: '#1B5E20', label: 'Approved' };
    }
    return { bgcolor: '#D84315', label: 'Pending' };
  };

  return (
    <Card
      sx={{
        minWidth: islistview ? null : '50%',
        borderRadius: 4,
        boxShadow: 3,
        overflow: 'hidden',
      }}
      onClick={() => {
        navigate(`/places/${id}`);
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="200"
          image={imageUrl}
          alt={title}
        />
        
        {isFavorited && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click
            handleFavoriteToggle(id);
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'white',
            '&:hover': { bgcolor: 'white' },
            boxShadow: 1,
            zIndex: 1,
          }}
        >
          {isLikedState ? <Favorite sx={{ color: 'red' }} /> : <FavoriteBorder />}
        </IconButton>
        )}

        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
          }}
        >
          {category.slice(0, 4).join(', ').split(', ').map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              size="small"
              color="default"
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 500,
                borderRadius: '6px',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(15px)',
                }
              }}
              variant='outlined'
            />
          ))}
        </Stack>
      </Box>

      {islistview && (
        <CardContent sx={{ flexDirection: 'row',display: 'flex',alignItems: 'space-between',justifyContent: 'space-between'}}>
          <Box>

          <Typography variant="subtitle1" fontWeight="bold">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {distance} 
            {/* • {category.slice(0, 4).join(', ')} */}
          </Typography>
          </Box>
          {status ? (
            <Chip
              label={getStatusStyles(status).label}
              sx={{
                mb: 2,
                bgcolor: getStatusStyles(status).bgcolor,
                color: 'white',
                borderRadius: 20,
                px: 1.5,
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, backgroundColor: getRatingColor(rating), color: 'white', paddingX: 2, paddingY: 1, borderRadius: 100 }}>
              <Typography color='white'>{rating}</Typography>
              <StarIcon sx={{ color: 'white' }} />
            </Box>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default PlaceCard;
